import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import TextField from 'components/TextField';

import { PayseraSettings_settings$key } from '__generated__/PayseraSettings_settings.graphql';
import { PayseraSettingsMutation } from '__generated__/PayseraSettingsMutation.graphql';
import { useErrorHandler } from 'lib';
import { InputAdornment } from '@mui/material';
import { PUBLIC_URL } from 'const';

type FormFields = {
  projectId: string;
  lang: string;
  currency: string;
  paytext: string;
  password: string;
  acceptUrl: string;
  cancelUrl: string;
  callbackUrl: string;
};

type Props = {
  settings: PayseraSettings_settings$key;
};

const PayseraSettings: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { paysera } = useFragment(
    graphql`
      fragment PayseraSettings_settings on Settings {
        paysera {
          projectId
          lang
          currency
          paytext
          password
          acceptUrl
          cancelUrl
          callbackUrl
        }
      }
    `,
    props.settings
  );

  const [update, updating] = useMutation<PayseraSettingsMutation>(graphql`
    mutation PayseraSettingsMutation($input: UpdateSettingsInput!) {
      updateSettings(input: $input) {
        ...EtgSettings_settings
      }
    }
  `);

  const initial = {
    projectId: paysera?.projectId ?? '',
    lang: paysera?.lang ?? '',
    currency: paysera?.currency ?? '',
    paytext: paysera?.paytext ?? '',
    password: paysera?.password ?? '',
    acceptUrl: paysera?.acceptUrl ?? '',
    cancelUrl: paysera?.cancelUrl ?? '',
    callbackUrl: paysera?.callbackUrl ?? '',
  };

  const handleSubmit = useCallback(
    (paysera: FormFields) => {
      update({
        variables: {
          input: { paysera },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField fullWidth name="projectId" label="Project ID" />
            <TextField fullWidth name="lang" label="Kalbos kodas" />
            <TextField fullWidth name="currency" label="Valiutos kodas" />

            <TextField
              fullWidth
              name="paytext"
              label="Apmokėjimo pavadinimas"
            />

            <TextField fullWidth name="password" label="Slaptažodis" />

            <TextField
              fullWidth
              name="acceptUrl"
              label="Accept URL"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{PUBLIC_URL}</InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="cancelUrl"
              label="Cancel URL"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{PUBLIC_URL}</InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="callbackUrl"
              label="Callback URL"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{PUBLIC_URL}</InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default PayseraSettings;
